import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  ArrowLeft, Users, UsersRound, FileDown, Plus, Trash2, 
  RefreshCw, Save, Copy, Check, Sparkles, GripVertical, User, Edit2, X, Upload,
  HandHeart, Eye, EyeOff, Key, CheckSquare, Square
} from 'lucide-react';
import { specialEventsService, charactersService, eventsService } from '../../services';
import PartyCard from '../../components/SpecialEvents/PartyCard';
import Button from '../../components/ui/Button';
import { LoadingPage } from '../../components/ui/Loading';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { formatDate } from '../../utils/dateUtils';
import {BUFF_CLASSES, BUFFS_MINIMO, getClassTypeTranslation} from "../../utils/characterTranslation";

const SpecialEventManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('participants');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [parties, setParties] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [copiedBuffRotation, setCopiedBuffRotation] = useState(false);
  const [copiedAttackTeams, setCopiedAttackTeams] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState('system'); // 'system' or 'manual'
  const [allCharacters, setAllCharacters] = useState([]);
  const [searchChar, setSearchChar] = useState('');
  const [manualForm, setManualForm] = useState({
    characterName: '',
    characterClass: '',
    characterLevel: 1,
    pilot: ''
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Import from event states
  const [showImportEventModal, setShowImportEventModal] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [importingFromEvent, setImportingFromEvent] = useState(false);

  // Edit pilot state
  const [editingPilot, setEditingPilot] = useState(null); // { participantId, currentPilot }

  // Modal de confirmação para trocar de aba
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);

  // Filtros de grupos
  const [partyFilters, setPartyFilters] = useState({
    sortBy: 'number', // 'number', 'name', 'members', 'avgLevel'
    filterBuff: 'all', // 'all', 'withBuff', 'withoutBuff'
    searchName: ''
  });

  // Lent characters state
  const [lentCharacters, setLentCharacters] = useState([]);
  const [lentCharactersLoading, setLentCharactersLoading] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  useEffect(() => {
    loadEventData();
  }, [id]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      setError('');

      const [eventData, participantsData] = await Promise.all([
        specialEventsService.getById(id),
        specialEventsService.getParticipants(id)
      ]);

      setEvent(eventData.event);
      setParticipants(participantsData.participants || []);

      // Carregar grupos salvos se existirem
      try {
        const partiesData = await specialEventsService.getParties(id);
        if (partiesData.parties && partiesData.parties.length > 0) {
          setParties(partiesData.parties);
        }
      } catch (err) {
        // Não há grupos salvos ainda
        console.log('Nenhum grupo salvo ainda');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadLentCharacters = async () => {
    try {
      setLentCharactersLoading(true);
      const data = await specialEventsService.getLentCharacters(id);
      setLentCharacters(data.lentCharacters || []);
    } catch (err) {
      console.error('Erro ao carregar personagens emprestados:', err);
      setLentCharacters([]);
    } finally {
      setLentCharactersLoading(false);
    }
  };

  const handleToggleLentReported = async (participationId, currentValue) => {
    try {
      await eventsService.updateLentReported(participationId, !currentValue);
      setLentCharacters(prev => prev.map(lc =>
        lc.id === participationId ? { ...lc, lentReported: !currentValue } : lc
      ));
      setSuccess('Status de reporte atualizado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar status de reporte');
    }
  };

  const togglePasswordVisibility = (participationId) => {
    setVisiblePasswords(prev => ({ ...prev, [participationId]: !prev[participationId] }));
  };

  const handleGenerateParties = async () => {
    if (participants.length === 0) {
      setError('Adicione participantes antes de gerar grupos');
      return;
    }

    try {
      setLoading(true);
      const data = await specialEventsService.generateParties(id);

      // Define o líder de cada grupo como o primeiro membro não-buff
      const partiesWithLeaders = (data.parties || []).map(party => {
        const firstRegularMember = party.members.find(m => {
          const p = participants.find(part => part.id === m.id);
          return !p?.isBuff;
        });
        return {
          ...party,
          leaderId: firstRegularMember?.id || party.members[0]?.id || null
        };
      });

      setParties(partiesWithLeaders);
      setHasUnsavedChanges(true);
      setSuccess('Grupos gerados com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar grupos');
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para definir o líder de um grupo (sempre o primeiro membro não-buff)
  const updatePartyLeader = (party) => {
    const firstRegularMember = party.members.find(m => {
      const p = participants.find(part => part.id === m.id);
      return !p?.isBuff;
    });
    party.leaderId = firstRegularMember?.id || party.members[0]?.id || null;
  };

  // Função para trocar de aba com verificação de mudanças não salvas
  const handleTabChange = (newTab) => {
    if (hasUnsavedChanges && activeTab === 'parties') {
      setPendingTab(newTab);
      setShowUnsavedWarning(true);
    } else {
      setActiveTab(newTab);
    }
  };

  // Confirma troca de aba perdendo as alterações
  const confirmTabChange = () => {
    setActiveTab(pendingTab);
    setShowUnsavedWarning(false);
    setPendingTab(null);
    setHasUnsavedChanges(false);
  };

  // Cancela troca de aba
  const cancelTabChange = () => {
    setShowUnsavedWarning(false);
    setPendingTab(null);
  };

  const handleDragEnd = (result) => {
    const {source, destination} = result;

    if (!destination) return;

    // Mesma posição exata - não faz nada
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Reordenação dentro do mesmo grupo
    if (source.droppableId === destination.droppableId && source.droppableId.startsWith('party-')) {
      const partyId = source.droppableId.replace('party-', '');
      const newParties = [...parties];
      const party = newParties.find(p => p.id === partyId);

      if (!party) return;

      // Remove o membro da posição original
      const [movedMember] = party.members.splice(source.index, 1);

      // Insere na nova posição
      party.members.splice(destination.index, 0, movedMember);

      // Atualiza positions
      party.members.forEach((m, idx) => m.position = idx + 1);

      // Atualiza o líder (primeiro membro não-buff)
      updatePartyLeader(party);

      party.stats = calculatePartyStats(party.members);

      setParties(newParties);
      setHasUnsavedChanges(true);
      return;
    }

    // Drag de lista de buffs para um grupo
    if (source.droppableId === 'buff-participants') {
      const destPartyId = destination.droppableId.replace('party-', '');
      const newParties = [...parties];
      const destParty = newParties.find(p => p.id === destPartyId);

      if (!destParty) return;

      const buffList = getBuffParticipants();
      const participant = buffList[source.index];

      // Verifica se já existe no grupo
      const alreadyInParty = destParty.members.some(m => m.id === participant.id);
      if (alreadyInParty) {
        setError('Este buff já está neste grupo');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Buffs não contam no limite, então sempre pode adicionar
      const newMember = {
        id: participant.id,
        character: participant.character,
        participant: {pilot: participant.pilot}
      };

      destParty.members.splice(destination.index, 0, newMember);
      destParty.members.forEach((m, idx) => m.position = idx + 1);

      // Atualiza o líder (primeiro membro não-buff)
      updatePartyLeader(destParty);

      destParty.stats = calculatePartyStats(destParty.members);

      setParties(newParties);
      setHasUnsavedChanges(true);
      return;
    }

    // Drag de lista de participantes sem grupo para um grupo
    if (source.droppableId === 'unassigned-participants') {
      const destPartyId = destination.droppableId.replace('party-', '');
      const newParties = [...parties];
      const destParty = newParties.find(p => p.id === destPartyId);

      if (!destParty) return;

      const unassignedList = getUnassignedParticipants();
      const participant = unassignedList[source.index];

      // Verifica se já existe no grupo
      const alreadyInParty = destParty.members.some(m => m.id === participant.id);
      if (alreadyInParty) {
        setError('Este participante já está neste grupo');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Conta apenas participantes regulares (não-buff) no grupo de destino
      const regularMembersCount = destParty.members.filter(m => {
        const p = participants.find(part => part.id === m.id);
        return !p?.isBuff;
      }).length;

      // Valida limite para participantes regulares
      if (regularMembersCount >= 6) {
        setError('Grupo já possui 6 participantes regulares (buffs não contam no limite)');
        setTimeout(() => setError(''), 3000);
        return;
      }

      const newMember = {
        id: participant.id,
        character: participant.character,
        participant: {pilot: participant.pilot}
      };

      destParty.members.splice(destination.index, 0, newMember);
      destParty.members.forEach((m, idx) => m.position = idx + 1);

      // Atualiza o líder (primeiro membro não-buff)
      updatePartyLeader(destParty);

      destParty.stats = calculatePartyStats(destParty.members);

      setParties(newParties);
      setHasUnsavedChanges(true);
      return;
    }

    // Drag entre grupos
    const sourcePartyId = source.droppableId.replace('party-', '');
    const destPartyId = destination.droppableId.replace('party-', '');

    const newParties = [...parties];
    const sourceParty = newParties.find(p => p.id === sourcePartyId);
    const destParty = newParties.find(p => p.id === destPartyId);

    if (!sourceParty || !destParty) return;

    const movedMember = sourceParty.members[source.index];

    // Verifica se já existe no grupo de destino
    const alreadyInDestParty = destParty.members.some(m => m.id === movedMember.id);
    if (alreadyInDestParty) {
      setError('Este participante já está neste grupo');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Busca o participante original para verificar se é buff
    const originalParticipant = participants.find(p => p.id === movedMember.id);
    const isBuff = originalParticipant?.isBuff || false;

    if (isBuff) {
      // BUFF COMPARTILHADO: Duplica ao invés de mover (mantém no grupo original)
      const duplicatedMember = { ...movedMember };
      destParty.members.splice(destination.index, 0, duplicatedMember);
    } else {
      // NÃO-BUFF: Valida limite antes de mover
      const regularMembersInDest = destParty.members.filter(m => {
        const p = participants.find(part => part.id === m.id);
        return !p?.isBuff;
      }).length;

      if (regularMembersInDest >= 6) {
        setError('Grupo de destino já possui 6 participantes regulares (buffs não contam no limite)');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Move normalmente (remove do grupo original)
      sourceParty.members.splice(source.index, 1);
      destParty.members.splice(destination.index, 0, movedMember);
      sourceParty.members.forEach((m, idx) => m.position = idx + 1);

      // Atualiza líder do grupo de origem (primeiro membro não-buff)
      updatePartyLeader(sourceParty);
      sourceParty.stats = calculatePartyStats(sourceParty.members);
    }

    // Atualiza positions e stats do destino
    destParty.members.forEach((m, idx) => m.position = idx + 1);

    // Atualiza o líder do grupo de destino (primeiro membro não-buff)
    updatePartyLeader(destParty);
    destParty.stats = calculatePartyStats(destParty.members);

    setParties(newParties);
    setHasUnsavedChanges(true);
  };

  const calculatePartyStats = (members) => {
    let totalLevel = 0; // soma apenas do time fixo (sem buff rotativo)
    let buffCount = 0;
    let regularCount = 0;
    let sharedBuffCount = 0;

    members.forEach(member => {
      const participant = participants.find(p => p.id === member.id);
      const isSharedBuff = participant?.isBuff || false;

      if (isSharedBuff) {
        sharedBuffCount++;
      } else {
        regularCount++;
        totalLevel += member.character?.level || 0;
      }

      if (BUFF_CLASSES.includes(member.character?.class?.toUpperCase())) {
        buffCount++;
      }
    });

    return {
      memberCount: members.length,
      regularCount,
      sharedBuffCount,
      averageLevel: regularCount > 0 ? Math.round(totalLevel / regularCount) : 0,
      buffCount
    };
  };

  const sortByClassAndLevel = (a, b) => {
    const classA = a.character?.class?.toUpperCase() || '';
    const classB = b.character?.class?.toUpperCase() || '';

    if (classA !== classB) {
      return classA.localeCompare(classB);
    }

    const levelA = a.character?.level || 0;
    const levelB = b.character?.level || 0;

    // Level DESC (maior level primeiro)
    return levelB - levelA;
  };

  const getUnassignedParticipants = () => {
    const assignedIds = new Set();

    parties.forEach(party => {
      party.members.forEach(member => {
        const participant = participants.find(p => p.id === member.id);
        if (!participant?.isBuff) {
          assignedIds.add(member.id);
        }
      });
    });

    return participants
      .filter(p => !assignedIds.has(p.id) && !p.isBuff)
      .sort(sortByClassAndLevel);
  };

  const getBuffParticipants = () => {
    return participants
      .filter(p => p.isBuff)
      .sort(sortByClassAndLevel);
  };

  const partyHasAllMinBuffs = (party) => {
    const classesNoGrupo = party.members
      ?.map(m => m.character?.class?.toUpperCase())
      .filter(Boolean) || [];

    return BUFFS_MINIMO.every(buffClass =>
      classesNoGrupo.includes(buffClass)
    );
  };

  const isIncompleteGroup = (party) => {
    const membrosValidos = party.members?.filter(member => !member.character.isBuff) || [];

    return membrosValidos.length < 5;
  };

  const getFilteredAndSortedParties = () => {
    let filtered = [...parties];

    // Filtro por nome
    if (partyFilters.searchName) {
      filtered = filtered.filter(party =>
        party.partyNumber?.toString()?.toLowerCase().includes(partyFilters.searchName.toLowerCase())
      );
    }

    // Filtro por buff
    if (partyFilters.filterBuff !== 'all') {
      filtered = filtered.filter(party => {
        if (partyFilters.filterBuff === 'incomplete') {
          return isIncompleteGroup(party);
        }

        const hasAllMinBuffs = partyHasAllMinBuffs(party);

        return partyFilters.filterBuff === 'withBuff'
          ? hasAllMinBuffs
          : !hasAllMinBuffs;
      });
    }

    // Ordenação
    filtered.sort((a, b) => {
      switch (partyFilters.sortBy) {
        case 'name':
          return (a.partyNumber.toString() || '').localeCompare(b.partyNumber.toString() || '');
        case 'members':
          return (b.stats?.memberCount || 0) - (a.stats?.memberCount || 0);
        case 'avgLevel':
          return (b.stats?.averageLevel || 0) - (a.stats?.averageLevel || 0);
        case 'number':
        default:
          return a.partyNumber - b.partyNumber;
      }
    });

    return filtered;
  };

  const handleCreateManualParty = () => {
    const newParty = {
      id: `manual_party_${Date.now()}`,
      partyNumber: parties.length + 1,
      partyName: `Party ${parties.length + 1}`,
      leaderId: null,
      members: [],
      stats: { memberCount: 0, averageLevel: 0, buffCount: 0 }
    };

    setParties([...parties, newParty]);
    setHasUnsavedChanges(true);
  };

  const handleRemoveParty = (partyId) => {
    if (!window.confirm('Remover este grupo?')) return;

    const newParties = parties.filter(p => p.id !== partyId);
    // Renumera grupos
    newParties.forEach((party, index) => {
      party.partyNumber = index + 1;
      if (!party.partyName || party.partyName.startsWith('Party ')) {
        party.partyName = `Party ${index + 1}`;
      }
    });

    setParties(newParties);
    setHasUnsavedChanges(true);
  };

  const handleRemoveMemberFromParty = (partyId, memberId) => {
    const newParties = [...parties];
    const party = newParties.find(p => p.id === partyId);

    if (!party) return;

    party.members = party.members.filter(m => m.id !== memberId);
    party.members.forEach((m, idx) => m.position = idx + 1);

    // Atualiza o líder (primeiro membro não-buff)
    updatePartyLeader(party);

    party.stats = calculatePartyStats(party.members);

    setParties(newParties);
    setHasUnsavedChanges(true);
  };

  const handleUpdatePilot = async (participantId, newPilot) => {
    try {
      setLoading(true);
      await specialEventsService.updateParticipant(id, participantId, {pilot: newPilot});

      // Atualiza localmente
      setParticipants(prev => prev.map(p => 
        p.id === participantId ? { ...p, pilot: newPilot } : p
      ));

      setEditingPilot(null);
      setSuccess('Piloto atualizado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar piloto');
    } finally {
      setLoading(false)
    }
  };

  const handleSaveParties = async () => {
    try {
      setLoading(true);

      const formattedParties = parties.map(party => ({
        partyNumber: party.partyNumber,
        partyName: party.partyName || `Party ${party.partyNumber}`,
        leaderId: party.leaderId || (party.members[0]?.id),
        members: party.members.map(m => ({
          participantId: m.id  // m.id é o ID do participante
        }))
      }));

      console.log('Salvando grupos:', formattedParties);
      await specialEventsService.saveParties(id, formattedParties);
      setHasUnsavedChanges(false);
      setSuccess('Grupos salvos com sucesso!');
      setTimeout(() => setSuccess(''), 3000);

      // Recarrega grupos salvos
      const partiesData = await specialEventsService.getParties(id);
      setParties(partiesData.parties);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar grupos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (characterId) => {
    try {
      await specialEventsService.addParticipant(id, {characterId});
      setShowAddModal(false);
      setSearchChar('');
      loadEventData();
      setSuccess('Participante adicionado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao adicionar participante');
    }
  };

  const handleAddManualParticipant = async () => {
    if (!manualForm.characterName || !manualForm.characterClass) {
      setError('Nome e classe são obrigatórios');
      return;
    }

    try {
      await specialEventsService.addParticipant(id, manualForm);
      setShowAddModal(false);
      setManualForm({ characterName: '', characterClass: '', characterLevel: 1, pilot: '' });
      loadEventData();
      setSuccess('Participante adicionado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao adicionar participante');
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    if (!window.confirm('Remover este participante?')) return;

    try {
      setLoading(true);
      await specialEventsService.removeParticipant(id, participantId);
      loadEventData();
      setSuccess('Participante removido!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover participante');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBuff = async (participantId) => {
    try {
      setLoading(true);
      await specialEventsService.toggleBuffStatus(id, participantId);
      loadEventData();
      setSuccess('Status de buff atualizado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar status');
    } finally {
      setLoading(false);
    }
  };

  const handleImportSpreadsheet = async () => {
    if (!importFile) {
      setError('Selecione um arquivo CSV');
      return;
    }

    try {
      setImporting(true);
      const result = await specialEventsService.importParticipantsFromSpreadsheet(id, importFile);
      setShowImportModal(false);
      setImportFile(null);
      loadEventData();

      let message = result.message;
      if (result.errors && result.errors.length > 0) {
        message += '\n\nErros:\n' + result.errors.join('\n');
      }

      setSuccess(message);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao importar planilha');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    specialEventsService.downloadParticipantsTemplate();
  };

  const handleDownloadPilots = () => {
    specialEventsService.downloadPilotsCsv(id);
  };

  const handleImportFromEvent = async () => {
    if (!selectedEventId) {
      setError('Selecione um evento');
      return;
    }

    try {
      setImportingFromEvent(true);
      const result = await specialEventsService.importFromEvent(id, selectedEventId);

      setShowImportEventModal(false);
      setSelectedEventId('');
      loadEventData();

      setSuccess(result.message);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao importar participantes');
    } finally {
      setImportingFromEvent(false);
    }
  };

  const loadAvailableEvents = async () => {
    try {
      const data = await eventsService.getEvents({status: 'SCHEDULED', limit: 100});
      setAvailableEvents(data.events || []);
    } catch (err) {
      console.error('Erro ao carregar eventos:', err);
      setError('Erro ao carregar eventos disponíveis');
    }
  };

  const createWhatsAppLink = (phone, userName, characterName, eventName) => {
    if (!phone) return null;

    let cleanPhone = phone.replace(/[^0-9]/g, '');

    if (cleanPhone.length <= 11) {
      cleanPhone = '+55' + cleanPhone;
    }

    const nameFormatted = userName.split(' ').shift();

    // Cria mensagem personalizada
    const message = `Fala, ${nameFormatted}. Beleza? Vamos usar seu char (${characterName}) no ${eventName}. Você poderia me enviar a senha temporária, por favor? Se já passou pra outro líder, ignore.`;

    // Encode da mensagem para URL
    const encodedMessage = encodeURIComponent(message);

    return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
  };

  // Conta em quantos grupos cada buff compartilhado está
  const getBuffPartyCount = (participantId) => {
    if (!parties || parties.length === 0) return 0;
    
    let count = 0;
    parties.forEach(party => {
      if (party.members && party.members.some(m => m.id === participantId)) {
        count++;
      }
    });
    
    return count;
  };

  // Retorna estilo (classes Tailwind) baseado em quantos grupos o buff está
  // 0 = cinza | 1 = verde | 2 = azul | 3 = laranja | 4+ = vermelho
  const getBuffAlertStyle = (count) => {
    if (count === 0) {
      return {
        chip: 'bg-gray-500/20 border-gray-500/40',
        dragging: 'shadow-gray-500/50 ring-gray-500',
        text: 'text-gray-300',
        subtext: 'text-gray-400',
        icon: 'text-gray-400',
        dot: 'bg-gray-400',
        label: 'Nenhum grupo',
      };
    }
    if (count === 1) {
      return {
        chip: 'bg-green-500/20 border-green-500/40',
        dragging: 'shadow-green-500/50 ring-green-500',
        text: 'text-white',
        subtext: 'text-green-200',
        icon: 'text-green-400',
        dot: 'bg-green-400',
        label: '1 grupo',
      };
    }
    if (count === 2) {
      return {
        chip: 'bg-blue-500/20 border-blue-500/40',
        dragging: 'shadow-blue-500/50 ring-blue-500',
        text: 'text-white',
        subtext: 'text-blue-200',
        icon: 'text-blue-400',
        dot: 'bg-blue-400',
        label: '2 grupos',
      };
    }
    if (count === 3) {
      return {
        chip: 'bg-orange-500/20 border-orange-500/40',
        dragging: 'shadow-orange-500/50 ring-orange-500',
        text: 'text-white',
        subtext: 'text-orange-200',
        icon: 'text-orange-400',
        dot: 'bg-orange-400',
        label: '3 grupos',
      };
    }
    // 4 ou mais
    return {
      chip: 'bg-red-500/20 border-red-500/40',
      dragging: 'shadow-red-500/50 ring-red-500',
      text: 'text-white',
      subtext: 'text-red-200',
      icon: 'text-red-400',
      dot: 'bg-red-400',
      label: '4 ou mais',
    };
  };

  const handleExportParties = async () => {
    try {
      const data = await specialEventsService.exportParties(id);
      navigator.clipboard.writeText(data.text);
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao exportar grupos');
    }
  };

  const handleExportBuffRotation = async () => {
    try {
      const data = await specialEventsService.exportBuffRotation(id);
      navigator.clipboard.writeText(data.text);
      setCopiedBuffRotation(true);
      setTimeout(() => setCopiedBuffRotation(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao exportar buff rotativo');
    }
  };

  const handleExportAttackTeams = async () => {
    try {
      const data = await specialEventsService.exportAttackTeams(id);
      navigator.clipboard.writeText(data.text);
      setCopiedAttackTeams(true);
      setTimeout(() => setCopiedAttackTeams(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao exportar times de ataque');
    }
  };

  const loadAllCharacters = async () => {
    try {
      const data = await charactersService.getAllCharacters({ page: 1, limit: 9999, status: 'ACTIVE' });
      setAllCharacters(data.characters || []);
    } catch (err) {
      console.error('Erro ao carregar personagens:', err);
    }
  };

  const filteredChars = allCharacters.filter(char =>
    char.name.toLowerCase().includes(searchChar.toLowerCase()) &&
    !participants.some(p => p.characterId === char.id)
  );

  if (loading && !event) return <LoadingPage/>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/admin/special-events')}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-orange-400"/>
            </button>
            <div>
              <h1
                className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                🎮 {event?.name}
              </h1>
              <p className="text-gray-300 mt-1">
                {event?.eventType} • {event?.eventDate && formatDate(event.eventDate)}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/app/admin/special-events/${id}/edit`)}
            className="bg-white/5 hover:bg-white/10 border border-white/10"
          >
            Editar Evento
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-xl p-4">
          <p className="text-green-400">{success}</p>
        </div>
      )}

      {/* Tabs */}
      <div
        className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
        <div className="flex flex-wrap gap-3 border-b border-white/10 pb-4">
          <button
            onClick={() => handleTabChange('participants')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'participants'
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg shadow-orange-500/20'
                : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/10'
            }`}
          >
            <Users className="w-4 h-4"/>
            Participantes ({participants.length})
          </button>
          <button
            onClick={() => handleTabChange('parties')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'parties'
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg shadow-orange-500/20'
                : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/10'
            }`}
          >
            <UsersRound className="w-4 h-4"/>
            Grupos ({parties.length})
            {hasUnsavedChanges && <span className="ml-1 text-yellow-400">●</span>}
          </button>
          <button
            onClick={() => handleTabChange('export')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'export'
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg shadow-orange-500/20'
                : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/10'
            }`}
          >
            <FileDown className="w-4 h-4"/>
            Exportar
          </button>
          <button
            onClick={() => {
              handleTabChange('lent');
              loadLentCharacters();
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'lent'
                ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg shadow-amber-500/20'
                : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/10'
            }`}
          >
            <HandHeart className="w-4 h-4"/>
            Personagens Emprestados
          </button>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Aba Participantes */}
          {activeTab === 'participants' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Lista de Participantes</h2>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={handleDownloadTemplate}
                    className="bg-white/5 hover:bg-white/10 border border-white/10"
                    size="sm"
                  >
                    <FileDown className="w-4 h-4"/>
                    Baixar Template
                  </Button>
                  <Button
                    onClick={() => setShowImportModal(true)}
                    className="bg-blue-600/80 hover:bg-blue-500 border border-blue-500/50"
                    size="sm"
                  >
                    <Upload className="w-4 h-4"/>
                    Importar Planilha
                  </Button>
                  <Button
                    onClick={() => {
                      setShowImportEventModal(true);
                      loadAvailableEvents();
                    }}
                    className="bg-purple-600/80 hover:bg-purple-500 border border-purple-500/50"
                    size="sm"
                  >
                    <Users className="w-4 h-4"/>
                    Importar de Evento
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddModal(true);
                      loadAllCharacters();
                    }}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
                    size="sm"
                  >
                    <Plus className="w-4 h-4"/>
                    Adicionar
                  </Button>
                </div>
              </div>

              {participants.length === 0 ? (
                <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                  <Users className="w-16 h-16 mx-auto mb-4 text-orange-400/50"/>
                  <p className="text-gray-400">Nenhum participante adicionado ainda</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {participants.map(participant => (
                    <div key={participant.id}
                         className="bg-white/5 rounded-xl border border-white/10 p-4 hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10 transition-all">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">{participant.character?.name}</p>
                              {participant.isBuff && (
                                <Sparkles className="w-4 h-4 text-orange-400" title="Buff Compartilhado"/>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">
                              {participant.character?.class} • Lv {participant.character?.level}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleToggleBuff(participant.id)}
                              className={`p-2 rounded-lg border border-white/10 transition-all ${
                                participant.isBuff
                                  ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                                  : 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-orange-400'
                              }`}
                              title={participant.isBuff ? 'Remover buff compartilhado' : 'Marcar como buff compartilhado'}
                            >
                              <Sparkles className="w-4 h-4"/>
                            </button>
                            <button
                              onClick={() => handleRemoveParticipant(participant.id)}
                              className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 border border-white/10 transition-all"
                            >
                              <Trash2 className="w-4 h-4"/>
                            </button>
                          </div>
                        </div>

                        {/* Piloto Section */}
                        <div className="border-t border-white/10 pt-3">
                          {editingPilot?.participantId === participant.id ? (
                            <div className="flex gap-2">
                              <Input
                                value={editingPilot.currentPilot}
                                onChange={(e) => setEditingPilot({...editingPilot, currentPilot: e.target.value})}
                                placeholder="Nome do piloto"
                                className="flex-1 text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePilot(participant.id, editingPilot.currentPilot)}
                                className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 transition-all"
                                title="Salvar"
                              >
                                <Check className="w-4 h-4"/>
                              </button>
                              <button
                                onClick={() => setEditingPilot(null)}
                                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-gray-400"
                                title="Cancelar"
                              >
                                <X className="w-4 h-4"/>
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-500"/>
                                <span className="text-sm text-gray-400 flex-1">
                                {participant.pilot ? (
                                  <span className="text-orange-400">Piloto: {participant.pilot}</span>
                                ) : (
                                  <span>{participant.character?.user?.name || 'Dono do char'}</span>
                                )}
                              </span>
                                <button
                                  onClick={() => setEditingPilot({
                                    participantId: participant.id,
                                    currentPilot: participant.pilot || ''
                                  })}
                                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-gray-400 hover:text-orange-400"
                                  title="Editar piloto"
                                >
                                  <Edit2 className="w-3 h-3"/>
                                </button>
                              </div>
                              {participant.pilot && participant.character?.user?.phone && (
                                <a
                                  href={createWhatsAppLink(
                                    participant.character?.user.phone,
                                    participant.character?.user.name,
                                    participant.character.name || participant.characterName,
                                    event?.name
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 transition-all text-xs"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                  </svg>
                                  Solicitar senha via WhatsApp
                                </a>
                              )}
                              {participant.pilot && !participant.character?.user?.phone && (
                                <p className="text-xs text-gray-500 italic">
                                  ⚠️ Telefone não cadastrado
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Aba Grupos */}
          {activeTab === 'parties' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Grupos de Combate</h2>
                <div className="flex gap-2">
                  {hasUnsavedChanges && (
                    <span className="text-yellow-400 text-sm flex items-center gap-2">
                      <RefreshCw className="w-4 h-4"/>
                      Alterações não salvas
                    </span>
                  )}
                  <Button
                    onClick={handleCreateManualParty}
                    className="bg-white/5 hover:bg-white/10 border border-white/10"
                    size="sm"
                  >
                    <Plus className="w-4 h-4"/>
                    Criar Grupo Manual
                  </Button>
                  <Button
                    onClick={handleGenerateParties}
                    className="bg-white/5 hover:bg-white/10 border border-white/10"
                    size="sm"
                    disabled={loading}
                  >
                    <RefreshCw className="w-4 h-4"/>
                    Gerar Automaticamente
                  </Button>
                  {parties.length > 0 && (
                    <Button
                      onClick={handleSaveParties}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-lg shadow-green-500/20"
                      size="sm"
                      disabled={loading || !hasUnsavedChanges}
                    >
                      <Save className="w-4 h-4"/>
                      Salvar Grupos
                    </Button>
                  )}
                </div>
              </div>

              {/* Filtros e Ordenação */}
              {parties.length > 0 && (
                <div className="bg-white/5 rounded-xl border border-white/10 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Busca por nome */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Buscar por nome
                      </label>
                      <Input
                        placeholder="Digite o nome do grupo..."
                        value={partyFilters.searchName}
                        onChange={(e) => setPartyFilters({...partyFilters, searchName: e.target.value})}
                        className="text-sm"
                      />
                    </div>

                    {/* Ordenar por */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ordenar por
                      </label>
                      <Select
                        value={partyFilters.sortBy}
                        onChange={(e) => setPartyFilters({...partyFilters, sortBy: e.target.value})}
                      >
                        <option value="number">Número do Grupo</option>
                        <option value="name">Nome (A-Z)</option>
                        <option value="members">Nº de Membros</option>
                        <option value="avgLevel">Level Médio</option>
                      </Select>
                    </div>

                    {/* Filtrar por buff */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Filtrar grupos
                      </label>
                      <Select
                        value={partyFilters.filterBuff}
                        onChange={(e) => setPartyFilters({...partyFilters, filterBuff: e.target.value})}
                      >
                        <option value="all">Todos os Grupos</option>
                        <option value="withBuff">Com todos buff mínimo</option>
                        <option value="withoutBuff">Sem buffs mínimo</option>
                        <option value="incomplete">Grupos Incompletos</option>
                      </Select>
                    </div>
                  </div>

                  {/* Contador de resultados */}
                  <div className="mt-3 text-sm text-gray-400">
                    Mostrando {getFilteredAndSortedParties().length} de {parties.length} grupo(s)
                  </div>
                </div>
              )}

              {parties.length === 0 ? (
                <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                  <UsersRound className="w-16 h-16 mx-auto mb-4 text-orange-400/50"/>
                  <p className="text-gray-400 mb-4">Nenhum grupo criado ainda</p>
                  <Button
                    onClick={handleGenerateParties}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
                  >
                    <RefreshCw className="w-4 h-4"/>
                    Gerar Grupos Automaticamente
                  </Button>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  {/* Participantes sem grupo (apenas regulares) */}
                  {getUnassignedParticipants().length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-white mb-3">
                        Participantes sem Grupo ({getUnassignedParticipants().length})
                      </h3>
                      <Droppable droppableId="unassigned-participants" type="PARTICIPANT" direction="horizontal">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`flex flex-wrap gap-2 p-4 rounded-xl border-2 border-dashed transition-colors min-h-[80px] ${
                              snapshot.isDraggingOver
                                ? 'border-blue-500 bg-blue-500/10'
                                : 'border-white/20 bg-white/5'
                            }`}
                          >
                            {getUnassignedParticipants().map((participant, index) => (
                              <Draggable
                                key={participant.id}
                                draggableId={`unassigned-${participant.id}`}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 transition-all ${
                                      snapshot.isDragging ? 'shadow-xl shadow-blue-500/50 ring-2 ring-blue-500' : ''
                                    }`}
                                  >
                                    <GripVertical className="w-3 h-3 text-gray-500"/>
                                    <span className="text-white text-sm font-medium">
                                      {participant.character?.name}
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                      {participant.character?.class} {participant.character?.level}
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}

                  {/* Participantes de Buff (separados) */}
                  {getBuffParticipants().length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-orange-400"/>
                        Participantes de Buff ({getBuffParticipants().length})
                      </h3>
                      <div className="bg-orange-500/5 border border-orange-500/30 rounded-xl p-3 mb-3">
                        <p className="text-orange-300 text-sm">
                          ✨ <strong>Buffs Compartilhados:</strong> Podem ser arrastados para múltiplos grupos
                        </p>
                      </div>
                      <Droppable droppableId="buff-participants" type="PARTICIPANT" direction="horizontal">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`flex flex-wrap gap-2 p-4 rounded-xl border-2 border-dashed transition-colors min-h-[80px] ${
                              snapshot.isDraggingOver
                                ? 'border-orange-500 bg-orange-500/10'
                                : 'border-orange-500/30 bg-orange-500/5'
                            }`}
                          >
                            {getBuffParticipants().map((participant, index) => {
                              const buffCount = getBuffPartyCount(participant.id);
                              const buffStyle = getBuffAlertStyle(buffCount);
                              return (
                              <Draggable
                                key={participant.id}
                                draggableId={`buff-${participant.id}`}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${buffStyle.chip} ${
                                      snapshot.isDragging ? `shadow-xl ring-2 ${buffStyle.dragging}` : ''
                                    }`}
                                  >
                                    <GripVertical className={`w-3 h-3 ${buffStyle.icon}`}/>
                                    <Sparkles className={`w-3 h-3 ${buffStyle.icon}`}/>
                                    <span className={`text-sm font-medium ${buffStyle.text}`}>
                                      {participant.character?.name}
                                    </span>
                                    <span className={`text-xs ${buffStyle.subtext}`}>
                                      {participant.character?.class} {participant.character?.level}
                                    </span>
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full border ${buffStyle.chip} ${buffStyle.text}`}>
                                      {buffCount}
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>

                      {/* Legenda de cores */}
                      <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3">
                        <p className="text-gray-300 text-xs font-semibold mb-2">
                          Legenda — em quantos grupos o buff está:
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {[0, 1, 2, 3, 4].map((count) => {
                            const legendStyle = getBuffAlertStyle(count);
                            return (
                              <div key={count} className="flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-full ${legendStyle.dot}`}/>
                                <span className="text-gray-300 text-xs">{legendStyle.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grupos */}
                  {getFilteredAndSortedParties().length === 0 ? (
                    <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                      <UsersRound className="w-16 h-16 mx-auto mb-4 text-gray-400/50"/>
                      <p className="text-gray-400">Nenhum grupo encontrado com os filtros aplicados</p>
                      <button
                        onClick={() => setPartyFilters({sortBy: 'number', filterBuff: 'all', searchName: ''})}
                        className="mt-4 text-orange-400 hover:text-orange-300 text-sm"
                      >
                        Limpar filtros
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getFilteredAndSortedParties().map((party, index) => (
                        <PartyCard
                          key={party.id}
                          party={{...party, stats: calculatePartyStats(party.members)}}
                          index={index}
                          onRemoveParty={handleRemoveParty}
                          onRemoveMember={handleRemoveMemberFromParty}
                          participants={participants}
                          eventName={event?.name || 'evento especial'}
                          allParties={parties}
                        />
                      ))}
                    </div>
                  )}
                </DragDropContext>
              )}
            </div>
          )}

          {/* Aba Exportar */}
          {activeTab === 'export' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white">Exportar Grupos</h2>

              {parties.length === 0 ? (
                <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                  <FileDown className="w-16 h-16 mx-auto mb-4 text-orange-400/50"/>
                  <p className="text-gray-400">Gere grupos primeiro para exportar</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Button
                    onClick={handleExportParties}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20 mr-2"
                  >
                    {copiedText ? (
                      <>
                        <Check className="w-4 h-4"/>
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4"/>
                        Copiar Grupos
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleExportBuffRotation}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-500/20  mr-2"
                  >
                    {copiedBuffRotation ? (
                      <>
                        <Check className="w-4 h-4"/>
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4"/>
                        Copiar Buff Rotativo
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleExportAttackTeams}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 shadow-lg shadow-blue-500/20 mr-2"
                  >
                    {copiedAttackTeams ? (
                      <>
                        <Check className="w-4 h-4"/>
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4"/>
                        Copiar Time (TAG)
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleDownloadPilots}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 shadow-lg"
                    size="sm"
                  >
                    <FileDown className="w-4 h-4"/>
                    Baixar CSV pilotos
                  </Button>

                  <div className="bg-white/5 rounded-xl border border-white/10 p-6">
                    <div className="text-gray-300 text-sm font-mono whitespace-pre-wrap">
                      {`Time de ataque\n\n`}
                      <>
                        {Object.entries(
                          parties.reduce((acc, party) => {
                            if (party.partyNumber > 13) {
                              return acc;
                            }
                            party.members.forEach(member => {
                              if (member.character?.isBuff) return;

                              const playerClass = member.character.class;

                              acc[playerClass] = (acc[playerClass] || 0) + 1;
                            });

                            return acc;
                          }, {})
                        ).map(([classe, quantidade]) => (
                          <p key={classe}>
                            {getClassTypeTranslation(classe)}: {quantidade}
                          </p>
                        ))}
                      </>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Aba Personagens Emprestados */}
          {activeTab === 'lent' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <HandHeart className="w-5 h-5 text-amber-400" />
                  Personagens Emprestados
                </h2>
                <Button
                  onClick={loadLentCharacters}
                  className="bg-white/5 hover:bg-white/10 border border-white/10"
                  size="sm"
                  disabled={lentCharactersLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${lentCharactersLoading ? 'animate-spin' : ''}`}/>
                  Atualizar
                </Button>
              </div>

              {lentCharactersLoading ? (
                <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                  <RefreshCw className="w-8 h-8 mx-auto mb-4 text-amber-400 animate-spin" />
                  <p className="text-gray-400">Carregando personagens emprestados...</p>
                </div>
              ) : lentCharacters.length === 0 ? (
                <div className="bg-white/5 rounded-xl border border-white/10 text-center py-12">
                  <HandHeart className="w-16 h-16 mx-auto mb-4 text-amber-400/50" />
                  <p className="text-gray-400">Nenhum personagem emprestado encontrado</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Os personagens emprestados aparecerão aqui quando membros usarem a opção
                    "Não posso ir, mas empresto personagem" nos eventos regulares e forem importados para este evento especial.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                    <p className="text-sm text-amber-300">
                      <strong>{lentCharacters.length}</strong> personagem(ns) emprestado(s) neste evento.
                      As credenciais abaixo são visíveis apenas para administradores.
                    </p>
                  </div>

                  {lentCharacters.map(lc => (
                    <div
                      key={lc.id}
                      className={`bg-white/5 rounded-xl border p-4 transition-all ${
                        lc.lentReported
                          ? 'border-green-500/30 bg-green-500/5'
                          : 'border-white/10'
                      }`}
                    >
                      <div className="flex flex-col gap-3">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold">
                              {lc.character?.name?.[0] || '?'}
                            </div>
                            <div>
                              <p className="text-white font-medium">{lc.character?.name || 'Personagem'}</p>
                              <p className="text-gray-400 text-sm">
                                {lc.character?.class} • Nv {lc.character?.level}
                                {lc.user?.name && <span> • Dono: {lc.user.name}</span>}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                              lc.pilot !== ''
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                            }`}>
                              {lc.pilot ? (<span>Piloto: <strong>{lc.pilot}</strong></span>) : '⚠ Precisa de piloto'}
                            </span>
                          </div>
                        </div>

                        {/* Credentials (only for no pilot) */}
                        {!lc.lentHasPilot && (lc.lentLoginId || lc.lentTempPassword) && (
                          <div className="bg-slate-800/80 rounded-xl p-4 border border-white/5">
                            <div className="flex items-center gap-2 mb-3">
                              <Key className="w-4 h-4 text-amber-400" />
                              <span className="text-sm font-medium text-amber-300">Credenciais da Conta</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">ID de Login</label>
                                <div className="flex items-center gap-2">
                                  <code className="px-3 py-2 bg-black/30 rounded-lg text-cyan-300 text-sm flex-1 select-all">
                                    {lc.lentLoginId || '—'}
                                  </code>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Senha Temporária</label>
                                <div className="flex items-center gap-2">
                                  <code className="px-3 py-2 bg-black/30 rounded-lg text-cyan-300 text-sm flex-1 select-all">
                                    {visiblePasswords[lc.id] ? (lc.lentTempPassword || '—') : '••••••••'}
                                  </code>
                                  <button
                                    onClick={() => togglePasswordVisibility(lc.id)}
                                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                                    title={visiblePasswords[lc.id] ? 'Ocultar senha' : 'Mostrar senha'}
                                  >
                                    {visiblePasswords[lc.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Reported checkbox */}
                        <div className="flex items-center justify-between border-t border-white/10 pt-3">
                          <button
                            onClick={() => handleToggleLentReported(lc.id, lc.lentReported)}
                            className="flex items-center gap-2 text-sm transition-colors hover:text-white"
                          >
                            {lc.lentReported ? (
                              <CheckSquare className="w-5 h-5 text-green-400" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-500" />
                            )}
                            <span className={lc.lentReported ? 'text-green-400' : 'text-gray-400'}>
                              {lc.lentReported ? 'Personagem reportado ✓' : 'Marcar como reportado'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Aviso - Alterações Não Salvas */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl p-6 border border-yellow-500/30 shadow-2xl max-w-md w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-xl bg-yellow-500/20 border border-yellow-500/30">
                <span className="text-3xl">⚠️</span>
              </div>
              <div>
                <h3
                  className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent mb-2">
                  Alterações não salvas
                </h3>
                <p className="text-sm text-gray-300">
                  Você tem alterações não salvas nos grupos. Se trocar de aba agora, todas as alterações serão perdidas.
                </p>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-6">
              <p className="text-sm text-yellow-300">
                💡 <strong>Dica:</strong> Salve os grupos antes de trocar de aba para não perder seu trabalho.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelTabChange}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium transition-all shadow-lg shadow-green-500/20"
              >
                Cancelar e Salvar
              </button>
              <button
                onClick={confirmTabChange}
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 text-gray-300 hover:text-red-300 font-medium transition-all"
              >
                Descartar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Importar de Evento */}
      {showImportEventModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3
                className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                📋 Importar de Evento
              </h3>
              <button
                onClick={() => {
                  setShowImportEventModal(false);
                  setSelectedEventId('');
                }}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              >
                <span className="text-xl text-gray-300">✕</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                <p className="text-sm text-purple-300 mb-2">
                  ℹ️ <strong>Importação automática:</strong>
                </p>
                <ul className="text-xs text-gray-400 space-y-1 ml-4">
                  <li>• Importa todos os participantes confirmados</li>
                  <li>• Participantes duplicados serão ignorados</li>
                  <li>• Piloto será mantido se informado</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Selecione o Evento
                </label>
                <Select
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                >
                  <option value="">Selecione um evento...</option>
                  {availableEvents.map(evt => (
                    <option key={evt.id} value={evt.id}>
                      {evt.name} - {new Date(evt.startDate).toLocaleDateString('pt-BR')}
                    </option>
                  ))}
                </Select>
              </div>

              {selectedEventId && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                  <p className="text-sm text-green-300">
                    ✓ Evento selecionado
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowImportEventModal(false);
                    setSelectedEventId('');
                  }}
                  className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10"
                  disabled={importingFromEvent}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleImportFromEvent}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                  disabled={!selectedEventId || importingFromEvent}
                >
                  {importingFromEvent ? 'Importando...' : 'Importar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Importar Planilha */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3
                className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                📊 Importar Planilha
              </h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              >
                <span className="text-xl text-gray-300">✕</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-sm text-blue-300 mb-2">
                  ℹ️ <strong>Formato esperado (CSV):</strong>
                </p>
                <ul className="text-xs text-gray-400 space-y-1 ml-4">
                  <li>• Nome, Classe, Level, Piloto, É Buff?</li>
                  <li>• Piloto e "É Buff?" são opcionais</li>
                  <li>• Use "sim" ou "1" para marcar como buff</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Arquivo CSV
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full bg-white/5 text-white rounded-xl px-4 py-3 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600/80 file:text-white file:cursor-pointer hover:file:bg-blue-500"
                />
              </div>

              {importFile && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                  <p className="text-sm text-green-300">
                    ✓ Arquivo selecionado: <strong>{importFile.name}</strong>
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10"
                  disabled={importing}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleImportSpreadsheet}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500"
                  disabled={!importFile || importing}
                >
                  {importing ? 'Importando...' : 'Importar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3
                className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                ➕ Adicionar Participante
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddMode('system');
                  setSearchChar('');
                  setManualForm({characterName: '', characterClass: '', characterLevel: 1, pilot: ''});
                }}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              >
                <span className="text-xl text-gray-300">✕</span>
              </button>
            </div>

            {/* Tabs: Sistema / Manual */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setAddMode('system')}
                className={`flex-1 py-2 px-4 rounded-xl transition-all ${
                  addMode === 'system'
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                Do Sistema
              </button>
              <button
                onClick={() => setAddMode('manual')}
                className={`flex-1 py-2 px-4 rounded-xl transition-all ${
                  addMode === 'manual'
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                Manual
              </button>
            </div>

            {addMode === 'system' ? (
              <>
                <Input
                  placeholder="Buscar personagem..."
                  value={searchChar}
                  onChange={(e) => setSearchChar(e.target.value)}
                  className="mb-4"
                />

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredChars.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      Nenhum personagem encontrado
                    </div>
                  ) : (
                    filteredChars.map(char => (
                      <div
                        key={char.id}
                        onClick={() => handleAddParticipant(char.id)}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:border-orange-500/50 hover:bg-white/10 cursor-pointer transition-all"
                      >
                        <div>
                          <p className="text-white font-medium">{char.name}</p>
                          <p className="text-gray-400 text-sm">
                            {char.class} • Lv {char.level}
                          </p>
                        </div>
                        <Plus className="w-5 h-5 text-orange-400"/>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome do Personagem *
                  </label>
                  <Input
                    value={manualForm.characterName}
                    onChange={(e) => setManualForm({...manualForm, characterName: e.target.value})}
                    placeholder="Ex: Warrior123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Classe *
                  </label>
                  <Select
                    value={manualForm.characterClass}
                    onChange={(e) => setManualForm({...manualForm, characterClass: e.target.value})}
                  >
                    <option value="">Selecione...</option>
                    <option value="ARQUEIRA">Arqueira</option>
                    <option value="ASSASSINA">Assassina</option>
                    <option value="ATALANTA">Atalanta</option>
                    <option value="CAVALEIRO">Cavaleiro</option>
                    <option value="GUERREIRA">Guerreira</option>
                    <option value="LUTADOR">Lutador</option>
                    <option value="MAGO">Mago</option>
                    <option value="MECANICO">Mecânico</option>
                    <option value="PIKE">Pike</option>
                    <option value="SACERDOTISA">Sacerdotisa</option>
                    <option value="XAMA">Xamã</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Level
                  </label>
                  <Input
                    type="number"
                    value={manualForm.characterLevel}
                    onChange={(e) => setManualForm({...manualForm, characterLevel: parseInt(e.target.value) || 1})}
                    min="1"
                    max="999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Piloto (Opcional)
                  </label>
                  <Input
                    value={manualForm.pilot}
                    onChange={(e) => setManualForm({...manualForm, pilot: e.target.value})}
                    placeholder="Nome do jogador"
                  />
                </div>

                <Button
                  onClick={handleAddManualParticipant}
                  className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
                >
                  <Plus className="w-4 h-4"/>
                  Adicionar Participante
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialEventManager;
